import { makeStopId } from "./defaults";
import { hexToRgba, normalizeHexColor, resolveCssVar, rgbaToHex } from "./rgba";
import type { GradientStop } from "./types";

/**
 * sRGB-linear interpolation between two hex colors at t ∈ [0, 1].
 * v1 uses per-channel linear interpolation on 0..255 (no gamma decode).
 * `t` is clamped to [0, 1]. Returns a 6-char uppercase hex.
 */
export const interpolateColor = (a: string, b: string, t: number): string => {
	// Resolve CSS variable references to their computed hex values for interpolation.
	const ca = hexToRgba(resolveCssVar(a));
	const cb = hexToRgba(resolveCssVar(b));
	const tt = Math.max(0, Math.min(1, t));
	const r = Math.round(ca.r + (cb.r - ca.r) * tt);
	const g = Math.round(ca.g + (cb.g - ca.g) * tt);
	const bCh = Math.round(ca.b + (cb.b - ca.b) * tt);
	return rgbaToHex({ r, g, b: bCh, a: 1 }, false);
};

/** Linear interpolation of opacity at t ∈ [0, 1]. Output clamped to [0, 1]. */
export const interpolateOpacity = (a: number, b: number, t: number): number => {
	const tt = Math.max(0, Math.min(1, t));
	return Math.max(0, Math.min(1, a + (b - a) * tt));
};

/**
 * Create a new stop at `position` by interpolating from its neighbors in `stops`.
 * Does NOT assume input is sorted. `position` is returned verbatim (no clamping),
 * so out-of-range values still yield a valid stop whose color/opacity come from
 * the nearest boundary but whose position reflects the caller's request.
 * Throws when `stops` is empty; copies the sole stop when `stops.length === 1`.
 */
export const makeInterpolatedStop = (
	stops: GradientStop[],
	position: number,
): GradientStop => {
	if (stops.length === 0) {
		throw new Error(
			"makeInterpolatedStop: stops must contain at least one stop",
		);
	}

	const sorted = [...stops].sort((x, y) => x.position - y.position);

	if (sorted.length === 1) {
		const only = sorted[0];
		return {
			id: makeStopId(),
			color: normalizeHexColor(only.color),
			opacity: only.opacity,
			position,
		};
	}

	if (position <= sorted[0].position) {
		const src = sorted[0];
		return {
			id: makeStopId(),
			color: normalizeHexColor(src.color),
			opacity: src.opacity,
			position,
		};
	}

	const last = sorted[sorted.length - 1];
	if (position >= last.position) {
		return {
			id: makeStopId(),
			color: normalizeHexColor(last.color),
			opacity: last.opacity,
			position,
		};
	}

	// find the two stops straddling position
	let left = sorted[0];
	let right = sorted[sorted.length - 1];
	for (let i = 0; i < sorted.length - 1; i++) {
		if (sorted[i].position <= position && position <= sorted[i + 1].position) {
			left = sorted[i];
			right = sorted[i + 1];
			break;
		}
	}

	const span = right.position - left.position;
	const t = span === 0 ? 0 : (position - left.position) / span;

	return {
		id: makeStopId(),
		color: interpolateColor(left.color, right.color, t),
		opacity: interpolateOpacity(left.opacity, right.opacity, t),
		position,
	};
};
